import React, { useState, useRef, DragEvent, useEffect } from 'react';
import { Image, Video, Smile, Hash, AtSign, Calendar, Clock, Send, Save, X, Upload, Search } from 'lucide-react';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import {Badge} from '../ui/badge';
import PublishingWizard from '../components/PublishingWizard';
import AudioBar from '../components/AudioBar';
import TimelineEditor from '../components/TimelineEditor';

export default function PostComposer() {
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['Instagram', 'Facebook']);
  const [contentText, setContentText] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [hashtagPickerOpen, setHashtagPickerOpen] = useState(false);
  const [mentionPickerOpen, setMentionPickerOpen] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState('');
  const [hashtagSearchQuery, setHashtagSearchQuery] = useState('');
  const [selectedAudioTrack, setSelectedAudioTrack] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const hashtagPickerRef = useRef<HTMLDivElement>(null);
  const mentionPickerRef = useRef<HTMLDivElement>(null);

  const platforms = [
    { name: 'Instagram', color: 'bg-pink-500', limit: 2200 },
    { name: 'Facebook', color: 'bg-blue-500', limit: 63206 },
    { name: 'LinkedIn', color: 'bg-blue-600', limit: 3000 },
    { name: 'YouTube', color: 'bg-red-500', limit: 5000 },
    { name: 'Twitter', color: 'bg-sky-400', limit: 280 },
  ];

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles = Array.from(files).filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isValidSize = isImage ? file.size <= 10 * 1024 * 1024 : file.size <= 100 * 1024 * 1024;
      return (isImage || isVideo) && isValidSize;
    });

    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const calculateSEOScore = (text: string) => {
    if (!text.trim()) return 0;

    // Extract hashtags (words starting with #)
    const hashtagArray = text.match(/#[\w]+/g) || [];
    const count = hashtagArray.length;

    // Calculate score based on optimal hashtag count (5-10 is ideal)
    let score = 0;
    if (count === 0) {
      score = 0;
    } else if (count >= 1 && count <= 3) {
      score = count * 20; // 20, 40, 60
    } else if (count >= 4 && count <= 7) {
      score = 60 + ((count - 3) * 10); // 70, 80, 90, 100
    } else if (count >= 8 && count <= 10) {
      score = 100; // Optimal range
    } else {
      // More than 10 starts to decrease (spam-like)
      score = Math.max(70, 100 - ((count - 10) * 5));
    }

    return Math.min(100, Math.max(0, score));
  };

  const getSEOScoreColor = (score: number) => {
    if (score >= 80) return { text: 'text-green-600', bg: 'bg-green-50', label: 'Excellent', variant: 'success' as const };
    if (score >= 60) return { text: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Good', variant: 'warning' as const };
    if (score >= 40) return { text: 'text-orange-600', bg: 'bg-orange-50', label: 'Fair', variant: 'warning' as const };
    return { text: 'text-red-600', bg: 'bg-red-50', label: 'Poor', variant: 'error' as const };
  };

  const getHashtagStatus = (count: number) => {
    if (count === 0) return { label: 'None', variant: 'destructive' as const };
    if (count >= 1 && count <= 4) return { label: 'Add More', variant: 'destructive' as const };
    if (count >= 5 && count <= 10) return { label: 'Optimal', variant: 'default' as const };
    return { label: 'Too Many', variant: 'destructive' as const };
  };

  const seoScore = calculateSEOScore(contentText);
  const seoColors = getSEOScoreColor(seoScore);
  const hashtagCount = (contentText.match(/#[\w]+/g) || []).length;
  const hashtagStatus = getHashtagStatus(hashtagCount);

  const handleSaveDraft = () => {
    // Save draft functionality
    alert('Draft saved successfully! Your content has been saved and can be accessed later.');
  };

  const handlePublishNow = () => {
    if (!contentText.trim()) {
      alert('Please add content to your post before publishing.');
      return;
    }
    if (selectedPlatforms.length === 0) {
      alert('Please select at least one platform to publish to.');
      return;
    }

    // Open Publishing Wizard
    setWizardOpen(true);
  };

  const handleSchedulePost = () => {
    if (!contentText.trim()) {
      alert('Please add content to your post before scheduling.');
      return;
    }
    if (selectedPlatforms.length === 0) {
      alert('Please select at least one platform to schedule for.');
      return;
    }

    // Open Publishing Wizard
    setWizardOpen(true);
  };

  // Emoji categories and data
  const emojiCategories = [
    {
      name: 'Smileys & People',
      emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬']
    },
    {
      name: 'Animals & Nature',
      emojis: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🐢', '🐍', '🦎', '🐙', '🦑', '🦐']
    },
    {
      name: 'Food & Drink',
      emojis: ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈']
    },
    {
      name: 'Activity & Sports',
      emojis: ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷', '⛸', '🥌', '🎿', '⛷', '🏂', '🪂', '🏋️', '🤼', '🤸', '⛹️']
    },
    {
      name: 'Travel & Places',
      emojis: ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎', '🚓', '🚑', '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🦯', '🦽', '🦼', '🛴', '🚲', '🛵', '🏍', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇']
    },
    {
      name: 'Objects',
      emojis: ['⌚', '📱', '📲', '💻', '⌨️', '🖥', '🖨', '🖱', '🖲', '🕹', '🗜', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽', '🎞', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙', '🎚', '🎛', '🧭', '⏱', '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔋', '🔌']
    }
  ];

  // Trending hashtags
  const trendingHashtags = [
    { tag: '#SocialMedia', count: '2.4M' },
    { tag: '#Marketing', count: '1.8M' },
    { tag: '#ContentCreator', count: '1.5M' },
    { tag: '#DigitalMarketing', count: '1.2M' },
    { tag: '#SocialMediaMarketing', count: '980K' },
    { tag: '#InstagramTips', count: '850K' },
    { tag: '#ContentStrategy', count: '720K' },
    { tag: '#BrandAwareness', count: '650K' },
    { tag: '#Engagement', count: '580K' },
    { tag: '#SocialMediaManager', count: '520K' },
    { tag: '#MarketingStrategy', count: '490K' },
    { tag: '#ContentMarketing', count: '450K' },
    { tag: '#Influencer', count: '420K' },
    { tag: '#CreativeContent', count: '380K' },
    { tag: '#GrowthHacking', count: '340K' }
  ];

  // Mock Instagram profiles for mentions
  const instagramProfiles = [
    { username: 'socialsync_official', name: 'SocialSync', followers: '125K', avatar: '🟣', verified: true },
    { username: 'marketing_pro', name: 'Marketing Pro', followers: '98K', avatar: '🔵', verified: true },
    { username: 'content_wizard', name: 'Content Wizard', followers: '87K', avatar: '🟢', verified: false },
    { username: 'digital_guru', name: 'Digital Guru', followers: '76K', avatar: '🟠', verified: true },
    { username: 'brand_expert', name: 'Brand Expert', followers: '65K', avatar: '🔴', verified: false },
    { username: 'social_ninja', name: 'Social Ninja', followers: '54K', avatar: '🟡', verified: false },
    { username: 'creative_minds', name: 'Creative Minds', followers: '43K', avatar: '🟤', verified: true },
    { username: 'growth_masters', name: 'Growth Masters', followers: '32K', avatar: '⚫', verified: false }
  ];

  // Filter mentions based on search query
  const filteredProfiles = mentionSearchQuery
    ? instagramProfiles.filter(profile =>
        profile.username.toLowerCase().includes(mentionSearchQuery.toLowerCase()) ||
        profile.name.toLowerCase().includes(mentionSearchQuery.toLowerCase())
      )
    : instagramProfiles;

  // Filter hashtags based on search query
  const filteredHashtags = hashtagSearchQuery
    ? trendingHashtags.filter(hashtag =>
        hashtag.tag.toLowerCase().includes(hashtagSearchQuery.toLowerCase())
      )
    : trendingHashtags;

  // Insert emoji at cursor position
  const insertEmoji = (emoji: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = contentText.substring(0, start) + emoji + contentText.substring(end);
    setContentText(newText);

    // Reset cursor position after emoji
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);

    setEmojiPickerOpen(false);
  };

  // Insert hashtag
  const insertHashtag = (hashtag: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = contentText.substring(0, start) + hashtag + ' ' + contentText.substring(end);
    setContentText(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + hashtag.length + 1, start + hashtag.length + 1);
    }, 0);

    setHashtagPickerOpen(false);
    setHashtagSearchQuery('');
  };

  // Insert mention
  const insertMention = (username: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const mention = `@${username}`;
    const newText = contentText.substring(0, start) + mention + ' ' + contentText.substring(end);
    setContentText(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + mention.length + 1, start + mention.length + 1);
    }, 0);

    setMentionPickerOpen(false);
    setMentionSearchQuery('');
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setEmojiPickerOpen(false);
      }
      if (hashtagPickerRef.current && !hashtagPickerRef.current.contains(event.target as Node)) {
        setHashtagPickerOpen(false);
        setHashtagSearchQuery('');
      }
      if (mentionPickerRef.current && !mentionPickerRef.current.contains(event.target as Node)) {
        setMentionPickerOpen(false);
        setMentionSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold text-[#111827]">Compose Post</h1>
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="md" onClick={handleSaveDraft}>
              <Save size={16} className="mr-2" />
              Save Draft
            </Button>
            <Button variant="primary" size="md" onClick={handlePublishNow}>
              <Send size={16} className="mr-2" />
              Publish Now
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#6B7280]">Create and schedule content across your social media platforms</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Area - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Platform Selection */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827] mb-1">Select Platforms</h2>
              <p className="text-sm text-[#6B7280]">Choose where to publish this content</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {platforms.map((platform) => (
                  <button
                    key={platform.name}
                    onClick={() => togglePlatform(platform.name)}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      selectedPlatforms.includes(platform.name)
                        ? 'border-[#2563EB] bg-[#EFF6FF]'
                        : 'border-[#E5E7EB] bg-white hover:border-[#9CA3AF]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${platform.color} rounded flex items-center justify-center text-white font-semibold`}>
                        {platform.name[0]}
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-semibold text-[#111827]">{platform.name}</div>
                        <div className="text-xs text-[#6B7280]">{platform.limit} chars</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Media Upload */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827] mb-1">Media</h2>
              <p className="text-sm text-[#6B7280]">Add images or videos to your post</p>
            </div>
            <div className="p-6">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
                  isDragging
                    ? 'border-[#2563EB] bg-[#EFF6FF]'
                    : 'border-[#E5E7EB] hover:border-[#9CA3AF]'
                }`}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-[#F9FAFB] rounded-full flex items-center justify-center">
                  <Upload size={32} className="text-[#6B7280]" />
                </div>
                <div className="text-sm font-medium text-[#111827] mb-1">
                  Click to upload or drag and drop
                </div>
                <div className="text-sm text-[#6B7280]">
                  PNG, JPG, GIF up to 10MB or MP4 up to 100MB
                </div>
              </div>

              {/* Uploaded Files Preview */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="text-sm font-medium text-[#111827]">
                    Uploaded Files ({uploadedFiles.length})
                  </div>
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                      <div className="w-12 h-12 flex-shrink-0 bg-white border border-[#E5E7EB] rounded flex items-center justify-center">
                        {file.type.startsWith('image/') ? (
                          <Image size={20} className="text-[#6B7280]" />
                        ) : (
                          <Video size={20} className="text-[#6B7280]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#111827] truncate">
                          {file.name}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-[#6B7280] hover:text-[#EF4444] hover:bg-[#FEE2E2] rounded-md transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Audio Integration */}
          <AudioBar onAudioSelect={(track) => setSelectedAudioTrack(track)} />

          {/* Helper Banner - Shows when no audio is selected */}
          {!selectedAudioTrack && (
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white flex-shrink-0">
                  <span className="text-xl">💡</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-[#111827] mb-1">
                    Add Audio to See Timeline Editor
                  </h3>
                  <p className="text-xs text-[#6B7280] mb-2">
                    Click "Add Audio to Post" above to select music or sound effects. Once added, an interactive timeline will appear where you can position and trim your audio tracks.
                    {uploadedFiles.some(file => file.type.startsWith('video/')) && (
                      <span className="block mt-1 text-green-700 font-medium">
                        ✓ Video uploaded! It will preview in the timeline when you add audio.
                      </span>
                    )}
                  </p>
                  <a
                    href="/timeline-demo"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#F67570] hover:text-[#F45C56] transition-colors"
                  >
                    <span>View Timeline Demo</span>
                    <span>→</span>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Editor - Shows when audio is selected */}
          {selectedAudioTrack && (
            <TimelineEditor
              videoDuration={120} // Default 2 minutes, would be actual video duration in real app
              uploadedAudio={selectedAudioTrack}
              uploadedVideo={uploadedFiles.find(file => file.type.startsWith('video/')) || null}
              onAudioUpdate={(blocks) => console.log('Audio blocks updated:', blocks)}
            />
          )}

          {/* Content Editor */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827] mb-1">Content</h2>
              <p className="text-sm text-[#6B7280]">Write your post content</p>
            </div>
            <div className="p-6">
              <textarea
                ref={textareaRef}
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                placeholder="What do you want to share?"
                className="w-full h-48 p-4 text-sm border border-[#D1D5DB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#FED7D5] resize-none transition-all"
              />
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2 relative">
                  <button className="p-2 hover:bg-[#F9FAFB] rounded transition-colors" title="Add emoji" onClick={() => setEmojiPickerOpen(true)}>
                    <Smile size={20} className="text-[#6B7280]" />
                  </button>
                  <button className="p-2 hover:bg-[#F9FAFB] rounded transition-colors" title="Add hashtag" onClick={() => setHashtagPickerOpen(true)}>
                    <Hash size={20} className="text-[#6B7280]" />
                  </button>
                  <button className="p-2 hover:bg-[#F9FAFB] rounded transition-colors" title="Mention" onClick={() => setMentionPickerOpen(true)}>
                    <AtSign size={20} className="text-[#6B7280]" />
                  </button>

                  {/* Emoji Picker Dropdown */}
                  {emojiPickerOpen && (
                    <div
                      ref={emojiPickerRef}
                      className="absolute bottom-full left-0 mb-2 w-[380px] bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto"
                    >
                      <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4">
                        <h3 className="text-sm font-semibold text-[#111827]">Emoji Picker</h3>
                        <p className="text-xs text-[#6B7280] mt-1">Click any emoji to insert</p>
                      </div>
                      {emojiCategories.map((category, idx) => (
                        <div key={idx} className="p-4 border-b border-[#E5E7EB] last:border-b-0">
                          <h4 className="text-xs font-semibold text-[#6B7280] mb-3">{category.name}</h4>
                          <div className="grid grid-cols-10 gap-2">
                            {category.emojis.map((emoji, emojiIdx) => (
                              <button
                                key={emojiIdx}
                                onClick={() => insertEmoji(emoji)}
                                className="text-2xl hover:bg-[#F9FAFB] rounded p-1 transition-colors"
                                title={emoji}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Hashtag Picker Dropdown */}
                  {hashtagPickerOpen && (
                    <div
                      ref={hashtagPickerRef}
                      className="absolute bottom-full left-0 mb-2 w-[360px] bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 max-h-[440px] overflow-hidden"
                    >
                      <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4">
                        <h3 className="text-sm font-semibold text-[#111827] mb-3">Trending Hashtags</h3>
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                          <input
                            type="text"
                            value={hashtagSearchQuery}
                            onChange={(e) => setHashtagSearchQuery(e.target.value)}
                            placeholder="Search hashtags..."
                            className="w-full h-9 pl-9 pr-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#FED7D5] transition-all"
                          />
                        </div>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto p-3">
                        {filteredHashtags.length > 0 ? (
                          filteredHashtags.map((item, idx) => (
                            <button
                              key={idx}
                              onClick={() => insertHashtag(item.tag)}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#F9FAFB] rounded-md transition-colors text-left"
                            >
                              <div>
                                <div className="text-sm font-medium text-[#111827]">{item.tag}</div>
                                <div className="text-xs text-[#6B7280] mt-0.5">{item.count} posts</div>
                              </div>
                              <Hash size={16} className="text-[#9CA3AF]" />
                            </button>
                          ))
                        ) : (
                          <div className="text-center py-8 text-sm text-[#6B7280]">
                            No hashtags found
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mention Picker Dropdown */}
                  {mentionPickerOpen && (
                    <div
                      ref={mentionPickerRef}
                      className="absolute bottom-full left-0 mb-2 w-[360px] bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 max-h-[440px] overflow-hidden"
                    >
                      <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4">
                        <h3 className="text-sm font-semibold text-[#111827] mb-3">Tag Instagram Account</h3>
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                          <input
                            type="text"
                            value={mentionSearchQuery}
                            onChange={(e) => setMentionSearchQuery(e.target.value)}
                            placeholder="Search profiles..."
                            className="w-full h-9 pl-9 pr-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#FED7D5] transition-all"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto p-3">
                        {filteredProfiles.length > 0 ? (
                          filteredProfiles.map((profile, idx) => (
                            <button
                              key={idx}
                              onClick={() => insertMention(profile.username)}
                              className="w-full flex items-center gap-3 p-3 hover:bg-[#F9FAFB] rounded-md transition-colors text-left"
                            >
                              <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-lg">
                                {profile.avatar}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <div className="text-sm font-medium text-[#111827] truncate">{profile.name}</div>
                                  {profile.verified && (
                                    <svg className="w-3.5 h-3.5 text-[#2563EB] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                                      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                  )}
                                </div>
                                <div className="text-xs text-[#6B7280] truncate">@{profile.username} • {profile.followers} followers</div>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="text-center py-8 text-sm text-[#6B7280]">
                            No profiles found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-sm text-[#6B7280]">
                  {contentText.length} / 2200
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Schedule Settings */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827]">Schedule</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Publish Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="w-full h-10 pl-10 pr-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Publish Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full h-10 pl-10 pr-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] transition-all"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E7EB]">
                <Button variant="primary" size="md" fullWidth onClick={handleSchedulePost}>
                  <Calendar size={16} className="mr-2" />
                  Schedule Post
                </Button>
              </div>
            </div>
          </Card>

          {/* Post Preview */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827]">Preview</h2>
            </div>
            <div className="p-6">
              {selectedPlatforms.length > 0 ? (
                <div className="space-y-4">
                  {selectedPlatforms.map((platform) => (
                    <div key={platform} className="p-4 border border-[#E5E7EB] rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="default">{platform}</Badge>
                      </div>
                      <div className="text-sm text-[#111827] whitespace-pre-wrap">
                        {contentText || 'Your content will appear here...'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-[#6B7280]">
                  Select platforms to preview
                </div>
              )}
            </div>
          </Card>

          {/* SEO Score */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827]">SEO Score</h2>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className={`text-5xl font-bold mb-2 ${seoColors.text}`}>{seoScore}</div>
                <div className="text-sm text-[#6B7280]">{seoColors.label}</div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      seoScore >= 80 ? 'bg-green-500' :
                      seoScore >= 60 ? 'bg-yellow-500' :
                      seoScore >= 40 ? 'bg-orange-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${seoScore}%` }}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Hashtags ({hashtagCount})</span>
                  <Badge variant={hashtagStatus.variant}>{hashtagStatus.label}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Content Length</span>
                  <Badge variant={contentText.length >= 100 && contentText.length <= 500 ? 'default' : 'destructive'}>
                    {contentText.length >= 100 && contentText.length <= 500 ? 'Optimal' : contentText.length < 100 ? 'Too Short' : 'Too Long'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Media Attached</span>
                  <Badge variant={uploadedFiles.length > 0 ? 'default' : 'destructive'}>
                    {uploadedFiles.length > 0 ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>

              {/* SEO Tips */}
              {seoScore < 80 && (
                <div className={`mt-4 p-3 rounded-lg ${seoColors.bg} border border-current ${seoColors.text}`}>
                  <p className="text-xs font-medium">
                    {hashtagCount === 0 && '💡 Add 5-10 hashtags to improve SEO score'}
                    {hashtagCount >= 1 && hashtagCount <= 4 && `💡 Add ${5 - hashtagCount} more hashtag${5 - hashtagCount > 1 ? 's' : ''} for optimal SEO`}
                    {hashtagCount > 10 && '💡 Consider reducing hashtags to 8-10 for best results'}
                    {hashtagCount >= 5 && hashtagCount <= 10 && contentText.length < 100 && '💡 Add more content (100+ chars recommended)'}
                    {hashtagCount >= 5 && hashtagCount <= 10 && contentText.length >= 100 && uploadedFiles.length === 0 && '💡 Add media to boost engagement'}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Publishing Wizard */}
      <PublishingWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        postData={{
          title: contentText.slice(0, 100) || 'Untitled Post',
          description: contentText,
          platforms: selectedPlatforms,
          mediaType: uploadedFiles.some(f => f.type.startsWith('video/')) ? 'video' : 'image',
          mediaFiles: uploadedFiles,
        }}
      />
    </div>
  );
}